import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { AlertModal, AlertModalProps } from '~/components/modals/AlertModal'
import { LoadingModal } from '~/components/modals/LoadingModal'
import OnboardingDialog from '~/components/modals/OnboardingDialog'
import AddWorkspaceModal from '~/components/modals/orgs/AddWorkspaceModal'
import { MonitoringDashboardDialog } from '~/components/monitoring/MonitoringDashboardDialog'
import AccountDialog from '~/components/plan/AccountDialog'
import CompleteDialog from '~/components/plan/CompleteDialog'
import ManageAccountDialog from '~/components/plan/ManageAccountDialog'
import PlanDialog from '~/components/plan/PlanDialog'
import ZeroCreditsDialog from '~/components/plan/ZeroCreditsDialog'
import { StripeCheckoutProvider } from '~/contexts/StripeCheckoutContext'
import { PersonalUsageDialog } from '~/components/usage-dashboard/PersonalUsageDialog'
import { useBillingContext } from '~/contexts/BillingContext'
import {
  ApiErrorType,
  ApiForbiddenErrorPayload,
  AppEvents,
  globalEventEmitter
} from '~/utils/shared'
import { BetterToast, ToastIds } from '~/utils/toast'

// ============================================================================
// Constants
// ============================================================================

const ERROR_MESSAGES = {
  [ApiErrorType.UPGRADE_REQUIRED]: {
    title: 'Upgrade Required',
    message: 'This feature is only available as part of the Enterprise package.'
  },
  [ApiErrorType.QUOTA_EXCEEDED_OVERAGE_DISABLED]: {
    title: 'Credit Limit Reached',
    message:
      "You've reached your credit limit for this billing period. To continue working, enable 'Allow Overage' in Manage Account > Licenses, or contact your account administrator to increase your limit."
  },
  [ApiErrorType.INSUFFICIENT_CREDITS]: {
    title: 'Insufficient Credits',
    message:
      'You have no credits remaining. To purchase more credits, go to Manage Account > Upgrade tab and select a credit package.'
  },
  [ApiErrorType.INSUFFICIENT_CREDITS + '_free']: {
    title: 'Upgrade Required',
    message: 'You have no credits remaining. Please purchase credits or upgrade your plan.'
  },
  [ApiErrorType.WORKSPACE_READ_ONLY]: {
    title: 'Workspace Access Restricted',
    message:
      "You're currently on a Flex plan, which supports only 'Personal Workspace'. This is a shared Team workspace, so you have read-only access. To keep working, upgrade your plan to Team or Enterprise for full access, or switch to your 'Personal Workspace'."
  }
}

// ============================================================================
// Types
// ============================================================================

export interface ModalData {
  planOpen: boolean
  alertOpen: boolean
  createWorkspaceOpen: boolean
  openPlan: () => void
  showErrorAlert: (errorType: ApiErrorType, message?: string) => void
  openAlert: (props: Omit<AlertModalProps, 'open' | 'onClose'>) => void
  closePlan: () => void
  closeAlert: () => void
  openCreateWorkspace: () => void
  closeCreateWorkspace: () => void
  openComplete: (options?: { pendingCreateWorkspace?: boolean }) => void
  closeComplete: () => void
  openManageAccount: () => void
  closeManageAccount: () => void
  openAccount: () => void
  closeAccount: () => void
  openOnboarding: () => void
  closeOnboarding: () => void
  /** Session-scoped per-workspace flag: true once auto-open has fired. Prevents
   *  re-opening after the user dismissed via "Do it later" within the same
   *  page-load, even when WorkspaceValidationComponent remounts (e.g. user
   *  navigates to `/` and back). Cleared on full refresh. */
  wasOnboardingShownFor: (workspaceUuid: string) => boolean
  markOnboardingShownFor: (workspaceUuid: string) => void
  openLoading: (message?: string) => void
  closeLoading: () => void
  openPersonalUsage: () => void
  closePersonalUsage: () => void
  monitoringDashboardOpen: boolean
  openMonitoringDashboard: () => void
  closeMonitoringDashboard: () => void
  zeroCreditsOpen: boolean
  openZeroCredits: () => void
  closeZeroCredits: () => void
}

// ============================================================================
// Context
// ============================================================================

const ModalContext = createContext<ModalData | null>(null)

interface ModalProviderProps {
  children: ReactNode
}

export function ModalProvider({ children }: ModalProviderProps) {
  const { allLicenses, isBillingAdmin } = useBillingContext()

  // State
  const [planOpen, setPlanOpen] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertProps, setAlertProps] = useState<Omit<AlertModalProps, 'open' | 'onClose'> | null>(
    null
  )
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [manageAccountOpen, setManageAccountOpen] = useState(false)
  const [manageAccountInitialTab, setManageAccountInitialTab] = useState<
    'usage' | 'users' | 'account' | 'license' | 'billing' | 'upgrade' | undefined
  >(undefined)
  const [accountOpen, setAccountOpen] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [loadingOpen, setLoadingOpen] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('Loading...')
  const [personalUsageOpen, setPersonalUsageOpen] = useState(false)
  const [monitoringDashboardOpen, setMonitoringDashboardOpen] = useState(false)
  const [zeroCreditsOpen, setZeroCreditsOpen] = useState(false)
  const [pendingCreateWorkspace, setPendingCreateWorkspace] = useState(false)

  // ============================================================================
  // Alert Modal
  // ============================================================================

  const openAlert = useCallback((props: Omit<AlertModalProps, 'open' | 'onClose'>) => {
    setAlertProps(props)
    setAlertOpen(true)
  }, [])

  const closeAlert = useCallback(() => {
    setAlertOpen(false)
    setAlertProps(null)
  }, [])

  // ============================================================================
  // Error Alert (handles all API error types)
  // ============================================================================

  const showErrorAlert = useCallback(
    (errorType: ApiErrorType, message?: string) => {
      const isFreePlan = allLicenses?.is_free_license ?? false

      // Flex plan users can purchase credits
      const isInsufficientCreditsOnFree =
        errorType === ApiErrorType.INSUFFICIENT_CREDITS && isFreePlan

      const configKey = isInsufficientCreditsOnFree
        ? ApiErrorType.INSUFFICIENT_CREDITS + '_free'
        : errorType
      const config = ERROR_MESSAGES[configKey]
      if (!config) {
        if (message) BetterToast.error({ message, id: ToastIds.ApiForbiddenError })
        return
      }

      const needsUpgradeAction =
        errorType === ApiErrorType.UPGRADE_REQUIRED ||
        errorType === ApiErrorType.WORKSPACE_READ_ONLY ||
        isInsufficientCreditsOnFree

      const needsPurchaseAction =
        errorType === ApiErrorType.INSUFFICIENT_CREDITS && !isFreePlan && isBillingAdmin

      openAlert({
        title: config.title,
        message: message || config.message,
        ...(needsUpgradeAction && {
          primaryAction: {
            label: 'Upgrade Plan',
            onClick: () => {
              closeAlert()
              setPlanOpen(true)
            }
          }
        }),
        ...(needsPurchaseAction && {
          primaryAction: {
            label: 'Purchase Credits',
            onClick: () => {
              closeAlert()
              setManageAccountInitialTab('upgrade')
              setManageAccountOpen(true)
            }
          }
        })
      })
    },
    [openAlert, closeAlert, allLicenses?.is_free_license, isBillingAdmin]
  )

  // ============================================================================
  // Other Modals
  // ============================================================================

  const openPlan = useCallback(() => setPlanOpen(true), [])
  const closePlan = useCallback(() => setPlanOpen(false), [])

  const openCreateWorkspace = useCallback(() => setCreateWorkspaceOpen(true), [])
  const closeCreateWorkspace = useCallback(() => setCreateWorkspaceOpen(false), [])

  const openComplete = useCallback((options?: { pendingCreateWorkspace?: boolean }) => {
    setCompleteOpen(true)
    if (options?.pendingCreateWorkspace) {
      setPendingCreateWorkspace(true)
    }
  }, [])
  const closeComplete = useCallback(() => {
    setCompleteOpen(false)
    if (pendingCreateWorkspace) {
      setPendingCreateWorkspace(false)
      // By the time the user dismisses CompleteDialog, billing data has refreshed
      setCreateWorkspaceOpen(true)
    }
  }, [pendingCreateWorkspace])

  const openManageAccount = useCallback(() => {
    setManageAccountInitialTab(undefined)
    setManageAccountOpen(true)
  }, [])
  const closeManageAccount = useCallback(() => {
    setManageAccountOpen(false)
    setManageAccountInitialTab(undefined)
  }, [])

  const openAccount = useCallback(() => setAccountOpen(true), [])
  const closeAccount = useCallback(() => setAccountOpen(false), [])

  const openOnboarding = useCallback(() => setOnboardingOpen(true), [])
  const closeOnboarding = useCallback(() => setOnboardingOpen(false), [])

  const onboardingShownForRef = useRef<Set<string>>(new Set())
  const wasOnboardingShownFor = useCallback(
    (workspaceUuid: string) => onboardingShownForRef.current.has(workspaceUuid),
    []
  )
  const markOnboardingShownFor = useCallback((workspaceUuid: string) => {
    onboardingShownForRef.current.add(workspaceUuid)
  }, [])

  const openLoading = useCallback((message?: string) => {
    setLoadingOpen(true)
    setLoadingMessage(message || 'Loading...')
  }, [])

  const closeLoading = useCallback(() => {
    setLoadingOpen(false)
    setLoadingMessage('Loading...')
  }, [])

  const openPersonalUsage = useCallback(() => setPersonalUsageOpen(true), [])
  const closePersonalUsage = useCallback(() => setPersonalUsageOpen(false), [])

  const openMonitoringDashboard = useCallback(() => setMonitoringDashboardOpen(true), [])
  const closeMonitoringDashboard = useCallback(() => setMonitoringDashboardOpen(false), [])

  const openZeroCredits = useCallback(() => setZeroCreditsOpen(true), [])
  const closeZeroCredits = useCallback(() => setZeroCreditsOpen(false), [])

  // ============================================================================
  // Event Listeners
  // ============================================================================

  useEffect(() => {
    const handleApiForbiddenError = (payload: ApiForbiddenErrorPayload) => {
      if (payload.errorType) {
        showErrorAlert(payload.errorType as ApiErrorType, payload.message)
      } else if (payload.message) {
        BetterToast.error({ message: payload.message, id: ToastIds.ApiForbiddenError })
      }
    }

    globalEventEmitter.on(AppEvents.ApiForbiddenError, handleApiForbiddenError)

    return () => {
      globalEventEmitter.off(AppEvents.ApiForbiddenError, handleApiForbiddenError)
    }
  }, [showErrorAlert])

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: ModalData = {
    planOpen,
    alertOpen,
    createWorkspaceOpen,
    openPlan,
    showErrorAlert,
    openAlert,
    closePlan,
    closeAlert,
    closeCreateWorkspace,
    openCreateWorkspace,
    openComplete,
    closeComplete,
    openManageAccount,
    closeManageAccount,
    openAccount,
    closeAccount,
    openOnboarding,
    closeOnboarding,
    wasOnboardingShownFor,
    markOnboardingShownFor,
    openLoading,
    closeLoading,
    openPersonalUsage,
    closePersonalUsage,
    monitoringDashboardOpen,
    openMonitoringDashboard,
    closeMonitoringDashboard,
    zeroCreditsOpen,
    openZeroCredits,
    closeZeroCredits
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <ModalContext.Provider value={contextValue}>
      <StripeCheckoutProvider>
        {children}

        {alertProps && <AlertModal open={alertOpen} onClose={closeAlert} {...alertProps} />}

        <PlanDialog isOpen={planOpen} onClose={closePlan} />
        <AddWorkspaceModal open={createWorkspaceOpen} handleClose={closeCreateWorkspace} />
        <CompleteDialog isOpen={completeOpen} onClose={closeComplete} />
        <ManageAccountDialog
          isOpen={manageAccountOpen}
          onClose={closeManageAccount}
          initialTab={manageAccountInitialTab}
        />
        <AccountDialog isOpen={accountOpen} onClose={closeAccount} />
        <OnboardingDialog isOpen={onboardingOpen} onClose={closeOnboarding} />
        <LoadingModal isOpen={loadingOpen} message={loadingMessage} />
        <PersonalUsageDialog isOpen={personalUsageOpen} onClose={closePersonalUsage} />
        <MonitoringDashboardDialog
          isOpen={monitoringDashboardOpen}
          onClose={closeMonitoringDashboard}
        />
        <ZeroCreditsDialog isOpen={zeroCreditsOpen} onClose={closeZeroCredits} />
      </StripeCheckoutProvider>
    </ModalContext.Provider>
  )
}

// ============================================================================
// Hook
// ============================================================================

export function useModalContext(): ModalData {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider')
  }
  return context
}
